import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useMyRecentEditedLoads } from '../../features/organization/api';
import { STATUS_OPTIONS } from '../../features/org-orders/manageLoadForm';

const statusLabel = (status: string) =>
  STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

type Props = {
  /** The Orders nav link (icon + label + badge) this flyout hangs off. */
  children: React.ReactNode;
  /** Where "View all orders" points — the remembered, filtered orders URL. */
  ordersUrl: string;
  /** Called after a flyout link is clicked (e.g. to close the mobile menu). */
  onNavigate?: () => void;
};

/**
 * Hover flyout on the Orders sidebar item listing the loads THIS user most
 * recently edited (scoped by audit actorUserId, so one admin never sees
 * another's edits). Clicking a load deep-links to `/orders?load=<id>`, which the
 * orders page turns into an open manage-load dialog.
 *
 * Rendered through a portal with fixed positioning: `.sidebar-nav` scrolls
 * (overflow), which would otherwise clip a flyout sitting to its right.
 */
export function OrdersRecentFlyout({ children, ordersUrl, onNavigate }: Props) {
  const anchorRef = useRef<HTMLLIElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  // Gates the fetch so nothing loads until the item is first hovered.
  const [primed, setPrimed] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const { data, isLoading } = useMyRecentEditedLoads({ limit: 5, enabled: primed });
  const loads = data?.loads ?? [];

  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = () => {
    cancelClose();
    setPrimed(true);

    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      // Keep the flyout on-screen if the item sits low in a scrolled nav.
      const top = Math.max(8, Math.min(rect.top, window.innerHeight - 280));
      setPos({ top, left: rect.right + 8 });
    }

    setOpen(true);
  };

  // A short delay lets the pointer cross the gap into the flyout without it
  // closing; entering the flyout cancels the timer.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  const closeNow = () => {
    cancelClose();
    setOpen(false);
    onNavigate?.();
  };

  useEffect(() => () => cancelClose(), []);

  return (
    <li ref={anchorRef} className="orders-nav" onMouseEnter={show} onMouseLeave={scheduleClose}>
      {children}

      {open &&
        pos &&
        createPortal(
          <div
            className="orders-nav__flyout"
            style={{ top: pos.top, left: pos.left }}
            role="menu"
            aria-label="Your recently edited loads"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className="orders-nav__flyout-head">Your recent edits</div>

            {isLoading && loads.length === 0 && (
              <div className="orders-nav__flyout-empty">Loading…</div>
            )}

            {!isLoading && loads.length === 0 && (
              <div className="orders-nav__flyout-empty">No recent edits yet</div>
            )}

            {loads.map((load) => (
              <NavLink
                key={load.itemId}
                to={`/orders?load=${load.itemId}`}
                className="orders-nav__load"
                role="menuitem"
                onClick={closeNow}
              >
                <span className="orders-nav__load-top">
                  <span className="orders-nav__load-number">
                    {load.loadNumber || `Item ${load.itemId.slice(-6)}`}
                  </span>
                  <span className="orders-nav__load-status">{statusLabel(load.status)}</span>
                </span>
                <span className="orders-nav__load-meta">
                  {[load.customerName, load.productName].filter(Boolean).join(' · ') || '—'}
                </span>
              </NavLink>
            ))}

            {loads.length > 0 && (
              <NavLink to={ordersUrl} className="orders-nav__all" onClick={closeNow}>
                View all orders
              </NavLink>
            )}
          </div>,
          document.body
        )}
    </li>
  );
}

export default OrdersRecentFlyout;
